import { trackDesignSystemAction } from 'analytics';
import { useCallback, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { IconType } from 'components/Icon';
import Icon from 'components/Icon';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { Loader } from 'components/Widget/Loader';
import { Tooltip } from 'components/Widget/Tooltip';
import VersionHistoryModal from 'components/Modal/VersionHistoryModal';
import { RootReducer } from 'redux/reducers';
import { selectActiveDesignSystem, selectActiveDesignSystemVersion } from 'redux/db/reducers';
import { showConfirmModal, setHistoricalDsVersionNumber } from 'redux/mode/actions';
import { designSystemEditingBlocked } from 'redux/db/util';
import { interceptIfNoCredits } from 'economy';
import { displayDsReadOnlyModal, displayPublishDsModal } from 'redux/mode/util';
import forge from 'apis/forge';
import { toast } from 'react-toastify';
import * as routes from 'routes';
import * as S from './styles';

const VersionDropdown = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {
		activeDesignSystem,
		activeDesignSystemVersion,
		dsVersionRefs,
		historicalDsVersionNumber,
		isPreparing,
	} = useSelector(
		(r: RootReducer) => ({
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeDesignSystemVersion: selectActiveDesignSystemVersion(r.db),
			dsVersionRefs: r.db.dsVersionRefs,
			historicalDsVersionNumber: r.mode.historicalDsVersionNumber,
			isPreparing: r.db.dsPreparing,
		}),
		shallowEqual
	);
	const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
	const [spinnerTooltipId] = useState(String(Math.random()));
	const [errorTooltipId] = useState(String(Math.random()));

	const versionNumber = activeDesignSystemVersion?.versionNumber ?? null;
	const latestPublishedVersionNumber =
		dsVersionRefs.length > 0 ? dsVersionRefs[0].versionNumber : null;
	const isDraft = versionNumber === 0 && !isPreparing;
	const isViewingHistorical = historicalDsVersionNumber !== null;

	const promptGenerationStatus = activeDesignSystemVersion?.manifest?.promptGenerationStatus;
	const showSpinner =
		promptGenerationStatus === 'pending' || promptGenerationStatus === 'generating';
	const showError = promptGenerationStatus === 'failed';

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onDiscard = useCallback(() => {
		if (!activeDesignSystem?.id) return;
		const blocked = designSystemEditingBlocked();
		if (blocked) {
			displayDsReadOnlyModal(blocked);
			return;
		}
		dispatch(
			showConfirmModal({
				title: `Discard changes to ${activeDesignSystem.title}?`,
				description:
					'All draft changes will be lost and the design system will revert to the last published version. This cannot be undone.',
				cancelLabel: 'Cancel',
				submitLabel: 'Discard Changes',
				onSubmit: async () => {
					try {
						await forge.discardDsVersion(activeDesignSystem.id);
						trackDesignSystemAction('revert');
						toast.success('Draft changes discarded');
					} catch (error) {
						toast.error('Failed to discard changes');
					}
				},
			})
		);
	}, [activeDesignSystem?.id, activeDesignSystem?.title, dispatch]);

	const onPublish = useCallback(() => {
		if (!activeDesignSystem?.id) return;
		const blocked = designSystemEditingBlocked();
		if (blocked) {
			displayDsReadOnlyModal(blocked);
			return;
		}
		if (interceptIfNoCredits({ kind: 'chatEdit', designSystemId: activeDesignSystem.id })) {
			return;
		}
		displayPublishDsModal(activeDesignSystem.id, activeDesignSystem.title);
	}, [activeDesignSystem?.id, activeDesignSystem?.title]);

	const onLoadVersion = useCallback(
		(vNum: number) => {
			trackDesignSystemAction('load_old_version', { version: vNum });
			dispatch(setHistoricalDsVersionNumber(vNum));
		},
		[dispatch]
	);

	const onBackToCurrent = useCallback(() => {
		dispatch(setHistoricalDsVersionNumber(null));
	}, [dispatch]);

	const onDuplicate = useCallback(() => {
		if (!activeDesignSystem?.id) return;
		const sourceVersion = isViewingHistorical ? historicalDsVersionNumber! : versionNumber!;
		const promise = (async () => {
			const res = await forge.duplicateDesignSystem({
				sourceDesignSystemId: activeDesignSystem.id,
				sourceVersionNumber: sourceVersion,
			});
			trackDesignSystemAction('duplicate');
			navigate(routes.designSystem(res.designSystem.id, res.designSystem.title));
		})();
		toast.promise(promise, {
			pending: 'Duplicating design system...',
			success: 'Design system duplicated',
			error: 'Failed to duplicate design system',
		});
	}, [
		activeDesignSystem?.id,
		isViewingHistorical,
		historicalDsVersionNumber,
		versionNumber,
		navigate,
	]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	// The version number currently being viewed (for checkmarks in the list)
	const activeVersionNumber = isViewingHistorical ? historicalDsVersionNumber : versionNumber;

	const { label, menuItems } = useMemo(() => {
		const recentVersionItems = dsVersionRefs
			.slice(0, 5)
			.map(
				(ref) =>
					new MenuItemAction(
						`v${ref.versionNumber}`,
						'',
						() => onLoadVersion(ref.versionNumber),
						false,
						activeVersionNumber === ref.versionNumber
					)
			);

		const allVersionsItems =
			dsVersionRefs.length > 5
				? [
						new MenuItemDivider(),
						new MenuItemAction('All versions...', '', () => setShowVersionHistoryModal(true)),
				  ]
				: [];

		const duplicateItem = new MenuItemAction('Duplicate as new DS', '', onDuplicate);

		let label: string;
		let menuItems: (MenuItemAction | MenuItemDivider)[] | null = null;

		if (isViewingHistorical) {
			label = `v${historicalDsVersionNumber}`;
			menuItems = [
				new MenuItemAction('Back to current', '', onBackToCurrent),
				duplicateItem,
				new MenuItemDivider(),
				...recentVersionItems,
				...allVersionsItems,
			];
		} else if (isDraft) {
			label = `v${latestPublishedVersionNumber! + 1} (draft)`;
			menuItems = [
				new MenuItemAction('Publish', '', onPublish),
				new MenuItemAction('Discard Changes', '', onDiscard),
				duplicateItem,
				...(recentVersionItems.length > 0 ? [new MenuItemDivider(), ...recentVersionItems] : []),
				...allVersionsItems,
			];
		} else if (isPreparing) {
			label = 'Preparing...';
		} else if (recentVersionItems.length > 0) {
			label = `v${versionNumber}`;
			menuItems = [
				duplicateItem,
				new MenuItemDivider(),
				...recentVersionItems,
				...allVersionsItems,
			];
		} else {
			label = `v${versionNumber ?? '?'}`;
		}

		return { label, menuItems };
	}, [
		dsVersionRefs,
		isViewingHistorical,
		isDraft,
		isPreparing,
		versionNumber,
		historicalDsVersionNumber,
		latestPublishedVersionNumber,
		activeVersionNumber,
		onLoadVersion,
		onDuplicate,
		onPublish,
		onDiscard,
		onBackToCurrent,
	]);

	const renderStatusIndicator = () => {
		if (!showSpinner && !showError) return null;
		return (
			<S.StatusIndicator>
				{showSpinner && (
					<>
						<span data-tooltip-id={spinnerTooltipId} data-tooltip-content="Preparing DS for use">
							<Loader width={14} height={14} />
						</span>
						<Tooltip id={spinnerTooltipId} placement="bottom" />
					</>
				)}
				{showError && (
					<>
						<span
							data-tooltip-id={errorTooltipId}
							data-tooltip-content="Preparing DS for use failed"
						>
							<Icon type={IconType.ERROR} width={14} height={14} />
						</span>
						<Tooltip id={errorTooltipId} placement="bottom" />
					</>
				)}
			</S.StatusIndicator>
		);
	};

	const renderDropdownMenu = (menuItems: (MenuItemAction | MenuItemDivider)[]) => {
		return (
			<DropdownMenu menuItems={menuItems}>
				<S.Base>
					{label}
					{renderStatusIndicator()}
					<Icon type={IconType.FILLED_ARROW_DOWN} width={12} height={12} />
				</S.Base>
			</DropdownMenu>
		);
	};

	const renderText = () => {
		return (
			<S.Text>
				{label}
				{renderStatusIndicator()}
			</S.Text>
		);
	};

	return (
		<>
			{menuItems ? renderDropdownMenu(menuItems) : renderText()}
			{showVersionHistoryModal && (
				<VersionHistoryModal
					onSelectVersion={(v: number) => {
						setShowVersionHistoryModal(false);
						onLoadVersion(v);
					}}
					onDuplicate={(v: number) => {
						setShowVersionHistoryModal(false);
						const promise = (async () => {
							const res = await forge.duplicateDesignSystem({
								sourceDesignSystemId: activeDesignSystem!.id,
								sourceVersionNumber: v,
							});
							trackDesignSystemAction('duplicate', { source: 'version_history_modal' });
							navigate(routes.designSystem(res.designSystem.id, res.designSystem.title));
						})();
						toast.promise(promise, {
							pending: 'Duplicating design system...',
							success: 'Design system duplicated',
							error: 'Failed to duplicate design system',
						});
					}}
					onClose={() => setShowVersionHistoryModal(false)}
				/>
			)}
		</>
	);
};

export default VersionDropdown;
